import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { RichTextEditor } from "../RichTextEditor";

/* =========================
   Types
========================= */
export type DayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type ActivitySchedule =
  | { mode: "RANGE"; startDate: string; endDate: string }
  | {
    mode: "REPEAT";
    startDate?: string;
    repeat:
    | { type: "WEEKLY"; daysOfWeek: DayOfWeek[] }
    | { type: "MONTHLY"; dayOfMonth: number };
  };

export type ActivityImageItem = {
  id: string;
  title?: string;
  imagePath?: string;
  file?: File;
  previewUrl?: string;
  orderSeq: number;
};

export type ActivityItem = {
  id?: string;
  title: string;
  descriptionHtml?: string;
  schedule: ActivitySchedule;
  images: ActivityImageItem[];
  isActive: boolean;
};

const DOW: { label: string; value: DayOfWeek }[] = [
  { label: "จ", value: 1 },
  { label: "อ", value: 2 },
  { label: "พ", value: 3 },
  { label: "พฤ", value: 4 },
  { label: "ศ", value: 5 },
  { label: "ส", value: 6 },
  { label: "อา", value: 7 },
];

type RepeatType = "WEEKLY" | "MONTHLY";

const uid = () =>
  crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

function formatScheduleLabel(s: ActivitySchedule) {
  if (s.mode === "RANGE") {
    const a = s.startDate || "—";
    const b = s.endDate || "—";
    return `ช่วงวันที่: ${a} → ${b}`;
  }
  const start = s.startDate ? `เริ่ม ${s.startDate} • ` : "";
  if (s.repeat.type === "WEEKLY") {
    const days = (s.repeat.daysOfWeek ?? [])
      .slice()
      .sort((x, y) => x - y)
      .map((d) => DOW.find((x) => x.value === d)?.label ?? String(d))
      .join(",");
    return `${start}ทุกสัปดาห์: ${days || "—"}`;
  }
  return `${start}ทุกเดือน: วันที่ ${s.repeat.dayOfMonth ?? 1}`;
}

/* =========================
   Tabs Editor
========================= */
export function ActivityTabsEditor({
  value,
  onChange,
}: {
  value: ActivityItem[];
  onChange: (next: ActivityItem[]) => void;
}) {
  const items = value ?? [];
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");

  useEffect(() => {
    if (!items.length) return setActiveId("");
    if (!items.some((x) => x.id === activeId)) setActiveId(items[0].id!);
  }, [items, activeId]);

  const activeIndex = useMemo(
    () => items.findIndex((x) => x.id === activeId),
    [items, activeId]
  );
  const active = activeIndex >= 0 ? items[activeIndex] : undefined;

  const addItem = () => {
    const next: ActivityItem = {
      id: uid(),
      title: `กิจกรรม ${items.length + 1}`,
      descriptionHtml: "",
      images: [],
      isActive: true,
      schedule: {
        mode: "REPEAT",
        startDate: "",
        repeat: { type: "WEEKLY", daysOfWeek: [1] },
      },
    };
    onChange([...items, next]);
    setActiveId(next.id!);
  };

  const removeActive = () => {
    if (!active) return;
    const next = items.filter((x) => x.id !== active.id);
    onChange(next);
    setActiveId(next[0]?.id ?? "");
  };

  const updateActive = (patch: Partial<ActivityItem>) => {
    if (!active) return;
    onChange(items.map((x) => (x.id === active.id ? { ...x, ...patch } : x)));
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography fontWeight={800}>ตารางกิจกรรม</Typography>
            <Typography variant="body2" color="text.secondary">
              แต่ละกิจกรรมอยู่ในแท็บ • เพิ่ม/ลบ ได้
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<AddIcon />} onClick={addItem} variant="contained" sx={{ borderRadius: 2 }}>
              เพิ่ม
            </Button>
            <Tooltip title="ลบกิจกรรม">
              <span>
                <IconButton onClick={removeActive} disabled={!active} sx={{ borderRadius: 2 }}>
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      {/* Tabs */}
      <Box
        sx={{
          px: 1,
          bgcolor: "background.paper",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          minWidth: 0,
        }}
        onWheel={(e) => {
          const el = e.currentTarget;
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            el.scrollLeft += e.deltaY;
            e.preventDefault();
          }
        }}
      >
        <Tabs
          value={activeId}
          onChange={(_, v) => setActiveId(v)}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            "& .MuiTabs-scroller": { overflow: "visible !important" },
            "& .MuiTabs-flexContainer": { width: "max-content", gap: 0.75 },
            "& .MuiTab-root": {
              minHeight: 44,
              textTransform: "none",
              borderRadius: 2,
              px: 1.25,
              flexShrink: 0,
            },
          }}
        >
          {items.map((it, i) => (
            <Tab
              key={it.id}
              value={it.id}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`#${i + 1}`} variant="outlined" sx={{ borderRadius: 2 }} />
                  <Typography sx={{ maxWidth: 160 }} noWrap>
                    {it.title || "ไม่มีชื่อ"}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </Tabs>
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {!active ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography fontWeight={800}>ยังไม่มีกิจกรรม</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              กด “เพิ่ม” เพื่อสร้างกิจกรรมใหม่
            </Typography>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={addItem} sx={{ borderRadius: 2 }}>
              เพิ่มกิจกรรม
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
              <TextField
                label="ชื่อกิจกรรม"
                value={active.title}
                onChange={(e) => updateActive({ title: e.target.value })}
                fullWidth
                sx={{
                  maxWidth: { sm: 360, md: 750 }, // 👈 บีบขนาด
                }}
              />
              {/* <Chip
                label={formatScheduleLabel(active.schedule)}
                variant="outlined"
                sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
              /> */}

              <FormControlLabel
                control={
                  <Switch
                    checked={active.isActive}
                    onChange={(e) => updateActive({ isActive: e.target.checked })}
                    color="success"
                  />
                }
                label={active.isActive ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                sx={{ ml: { sm: 1 } }}
              />

            </Stack>



            {/* Schedule */}
            <ActivityScheduleEditor
              value={active.schedule}
              onChange={(next) => updateActive({ schedule: next })}
            />

            {/* RichText */}
            <RichTextEditor
              value={active.descriptionHtml ?? ""}
              onChange={(html) => updateActive({ descriptionHtml: html })}
            />

            {/* Images */}
            <ActivityImagesEditor
              value={active.images ?? []}
              onChange={(imgs) => updateActive({ images: imgs })}
            />
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

/* =========================
   Schedule Editor
========================= */
function ActivityScheduleEditor({
  value,
  onChange,
}: {
  value: ActivitySchedule;
  onChange: (v: ActivitySchedule) => void;
}) {
  const mode: "RANGE" | "REPEAT" = value?.mode ?? "RANGE";

  const setMode = (m: "RANGE" | "REPEAT") => {
    if (m === "RANGE") onChange({ mode: "RANGE", startDate: "", endDate: "" });
    else onChange({ mode: "REPEAT", startDate: "", repeat: { type: "WEEKLY", daysOfWeek: [1] } });
  };

  const repeatType: RepeatType = value.mode === "REPEAT" ? value.repeat.type : "WEEKLY";

  const setRepeatType = (t: RepeatType) => {
    if (value.mode !== "REPEAT") return;
    if (t === "WEEKLY") onChange({ ...value, repeat: { type: "WEEKLY", daysOfWeek: [1] } });
    else onChange({ ...value, repeat: { type: "MONTHLY", dayOfMonth: 1 } });
  };

  const toggleDow = (d: DayOfWeek) => {
    if (value.mode !== "REPEAT") return;
    if (value.repeat.type !== "WEEKLY") return;

    const cur = value.repeat.daysOfWeek ?? [];
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
    const safe = next.length ? next : [d];
    onChange({ ...value, repeat: { type: "WEEKLY", daysOfWeek: safe } });
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, bgcolor: "background.default" }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={800}>กำหนดตาราง</Typography>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, v) => v && setMode(v)}
            size="small"
            sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "background.paper" }}
          >
            <ToggleButton value="RANGE">นิทรรศการพิเศษ</ToggleButton>
            <ToggleButton value="REPEAT">นิทรรศการประจำ</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {mode === "RANGE" ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="วันเริ่ม"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={value.mode === "RANGE" ? value.startDate : ""}
              onChange={(e) => value.mode === "RANGE" && onChange({ ...value, startDate: e.target.value })}
              fullWidth
            />
            <TextField
              label="วันสิ้นสุด"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={value.mode === "RANGE" ? value.endDate : ""}
              onChange={(e) => value.mode === "RANGE" && onChange({ ...value, endDate: e.target.value })}
              fullWidth
            />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="เริ่มใช้ตารางทำซ้ำตั้งแต่ (ไม่บังคับ)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={value.mode === "REPEAT" ? value.startDate ?? "" : ""}
              onChange={(e) => value.mode === "REPEAT" && onChange({ ...value, startDate: e.target.value })}
              fullWidth
            />

            <ToggleButtonGroup
              exclusive
              value={repeatType}
              onChange={(_, v) => v && setRepeatType(v)}
              size="small"
              sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "background.paper", width: "fit-content" }}
            >
              <ToggleButton value="WEEKLY">ทุกสัปดาห์</ToggleButton>
              <ToggleButton value="MONTHLY">ทุกเดือน</ToggleButton>
            </ToggleButtonGroup>

            {repeatType === "WEEKLY" ? (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  เลือกวันในสัปดาห์
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {DOW.map((d) => {
                    const selected =
                      value.mode === "REPEAT" &&
                      value.repeat.type === "WEEKLY" &&
                      (value.repeat.daysOfWeek ?? []).includes(d.value);

                    return (
                      <Chip
                        key={d.value}
                        label={d.label}
                        color={selected ? "primary" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        onClick={() => toggleDow(d.value)}
                        sx={{ cursor: "pointer", borderRadius: 2 }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            ) : (
              <TextField
                label="วันที่ของเดือน"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                value={
                  value.mode === "REPEAT" && value.repeat.type === "MONTHLY"
                    ? value.repeat.dayOfMonth ?? 1
                    : 1
                }
                onChange={(e) => {
                  if (value.mode !== "REPEAT") return;
                  if (value.repeat.type !== "MONTHLY") return;
                  const n = Number(e.target.value);
                  const clamped = Math.max(1, Math.min(31, Number.isFinite(n) ? n : 1));
                  onChange({ ...value, repeat: { type: "MONTHLY", dayOfMonth: clamped } });
                }}
                fullWidth
                helperText="เช่น 1 = วันที่ 1 ของทุกเดือน"
              />
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

/* =========================
   Images + Drag Sort
========================= */
function arrayMove<T>(arr: T[], from: number, to: number) {
  const next = [...arr];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

function SortableThumb({
  item,
  onRemove,
  onMoveLeft,
  onMoveRight,
  canLeft,
  canRight,
}: {
  item: ActivityImageItem;
  onRemove: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  canLeft: boolean;
  canRight: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        width: 140,
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 2,
        position: "relative",
        bgcolor: "background.paper",
      }}
    >
      <Box {...attributes} {...listeners} sx={{ height: 100, cursor: "grab", bgcolor: "grey.100" }}>
        {item.previewUrl || item.imagePath ? (
          <img
            src={item.previewUrl || item.imagePath}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            alt={item.title ?? "activity-image"}
          />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ImageIcon />
          </Box>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.45)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Stack direction="row" spacing={0.5} sx={{ p: 0.5 }}>
        <IconButton size="small" disabled={!canLeft} onClick={onMoveLeft}>
          <ArrowBackIosNewIcon fontSize="inherit" />
        </IconButton>
        <IconButton size="small" disabled={!canRight} onClick={onMoveRight}>
          <ArrowForwardIosIcon fontSize="inherit" />
        </IconButton>
      </Stack>
    </Box>
  );
}

function ActivityImagesEditor({
  value,
  onChange,
}: {
  value: ActivityImageItem[];
  onChange: (v: ActivityImageItem[]) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const base = value.length;
    const added: ActivityImageItem[] = Array.from(files).map((f, i) => ({
      id: uid(),
      file: f,
      previewUrl: URL.createObjectURL(f),
      title: f.name,
      orderSeq: base + i,
    }));
    onChange([...value, ...added].map((x, i) => ({ ...x, orderSeq: i })));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.default" }}>
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={800}>รูปภาพกิจกรรม</Typography>
          <Button component="label" variant="outlined" sx={{ borderRadius: 2 }}>
            เพิ่มรูป
            <input hidden type="file" multiple accept="image/*" onChange={(e) => addFiles(e.target.files)} />
          </Button>
        </Stack>

        {value.length === 0 ? (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff", boxShadow: 1, color: "text.secondary" }}>
            <Typography variant="body2">ยังไม่มีรูปภาพ</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return;
                const from = value.findIndex((x) => x.id === active.id);
                const to = value.findIndex((x) => x.id === over.id);
                onChange(arrayMove(value, from, to).map((x, i) => ({ ...x, orderSeq: i })));
              }}
            >
              <SortableContext items={value.map((x) => x.id)} strategy={horizontalListSortingStrategy}>
                <Stack direction="row" spacing={1} sx={{ width: "max-content" }}>
                  {value.map((it, idx) => (
                    <SortableThumb
                      key={it.id}
                      item={it}
                      onRemove={() => onChange(value.filter((x) => x.id !== it.id).map((x, i) => ({ ...x, orderSeq: i })))}
                      onMoveLeft={() => idx > 0 && onChange(arrayMove(value, idx, idx - 1).map((x, i) => ({ ...x, orderSeq: i })))}
                      onMoveRight={() => idx < value.length - 1 && onChange(arrayMove(value, idx, idx + 1).map((x, i) => ({ ...x, orderSeq: i })))}
                      canLeft={idx > 0}
                      canRight={idx < value.length - 1}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
